import axios from "axios";
import { showSnackbar } from "../utils/snackbar";

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}`,
  headers: {
    common: {
      "Content-Type": "application/json"
    }
  }
});

instance.interceptors.request.use(config => {
  config.headers.Authorization = `Bearer ${localStorage.getItem("user-token")}`;
  return config;
});

instance.interceptors.response.use(
  response => {
    // Check if the response indicates an error even with 200 status
    if (response.data && response.data.error === true && response.data.message) {
      console.log('[AXIOS INTERCEPTOR] Response with error flag detected:', {
        url: response.config?.url,
        method: response.config?.method,
        message: response.data.message
      });
      showSnackbar(response.data.message, "error");
      const apiError = new Error(response.data.message);
      // Mark as already displayed to prevent duplicate toasts
      apiError.__errorDisplayed = true;
      return Promise.reject(apiError);
    }
    return response;
  },
  error => {
    console.log('[AXIOS INTERCEPTOR] Error caught:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      hasErrorDisplayed: error.__errorDisplayed,
      originalMessage: error.message
    });

    let errorMessage = null;
    
    // Handle error responses
    if (error.response) {
      const errorData = error.response.data;
      
      // Check for the error format: {"error":true,"message":"...","data":{}}
      if (errorData && errorData.error === true && errorData.message) {
        errorMessage = errorData.message;
        console.log('[AXIOS INTERCEPTOR] Extracted error message from errorData.error format:', errorMessage);
      } else if (errorData && errorData.message) {
        // Fallback: if there's a message field, use it
        errorMessage = errorData.message;
        console.log('[AXIOS INTERCEPTOR] Extracted error message from errorData.message:', errorMessage);
      } else if (typeof errorData === "string") {
        // If error data is a string, use it directly
        errorMessage = errorData;
        console.log('[AXIOS INTERCEPTOR] Using errorData as string:', errorMessage);
      } else {
        // Generic error message
        errorMessage = error.response.statusText || "An error occurred";
        console.log('[AXIOS INTERCEPTOR] Using generic error message:', errorMessage);
      }
      
      // Show the error message in snackbar only if not already displayed
      if (!error.__errorDisplayed) {
        console.log('[AXIOS INTERCEPTOR] Calling showSnackbar from interceptor:', errorMessage);
        showSnackbar(errorMessage, "error");
        // Mark as already displayed to prevent duplicate toasts
        error.__errorDisplayed = true;
      } else {
        console.log('[AXIOS INTERCEPTOR] Skipping showSnackbar - error already displayed');
      }
      
      // Replace the error message so components see only the API message
      error.message = errorMessage;
    } else if (error.request) {
      // Network error
      errorMessage = "Network error. Please check your connection.";
      if (!error.__errorDisplayed) {
        console.log('[AXIOS INTERCEPTOR] Network error - calling showSnackbar');
        showSnackbar(errorMessage, "error");
        error.__errorDisplayed = true;
      }
      error.message = errorMessage;
    } else {
      // Other error - only show if it's not the default axios message
      if (error.message && !error.message.includes("Request failed with status code")) {
        errorMessage = error.message;
        if (!error.__errorDisplayed) {
          console.log('[AXIOS INTERCEPTOR] Other error - calling showSnackbar:', errorMessage);
          showSnackbar(errorMessage, "error");
          error.__errorDisplayed = true;
        }
      } else {
        errorMessage = "An error occurred";
        if (!error.__errorDisplayed) {
          console.log('[AXIOS INTERCEPTOR] Generic error - calling showSnackbar');
          showSnackbar(errorMessage, "error");
          error.__errorDisplayed = true;
        }
      }
      error.message = errorMessage;
    }
    
    return Promise.reject(error);
  }
);

export default instance;
